import React, { useEffect, useState } from 'react';
import { Search, Filter, Camera, Calendar, Clock, User, CheckCircle, Upload, Image as ImageIcon, MessageSquare, Star, AlertCircle, MapPin, Phone, Plus, Trash2, Edit, DollarSign, TrendingDown } from 'lucide-react';
type OrderStatus = 'pending' | 'inspection-scheduled' | 'inspected' | 'job-scheduled' | 'in-progress' | 'completed' | 'reviewed' | 'cancelled';
type CustomItem = {
  id: string;
  name: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
};
type Order = {
  id: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  serviceName: string;
  servicePrice: number;
  scheduledDate?: string;
  scheduledTime?: string;
  inspectionDate?: string;
  inspectionTime?: string;
  jobDate?: string;
  jobTime?: string;
  status: OrderStatus;
  createdAt: string;
  customerPhotos?: string[];
  picName?: string;
  picPhone?: string;
  picIdNumber?: string;
  beforePhotos?: string[];
  afterPhotos?: string[];
  progressUpdates?: {
    date: string;
    description: string;
    photos?: string[];
  }[];
  completedAt?: string;
  customerReview?: {
    rating: number;
    comment: string;
    date: string;
  };
  autoApprovedAt?: string;
  customItems?: CustomItem[];
  inspectionNotes?: string;
};
export function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showJobScheduleModal, setShowJobScheduleModal] = useState(false);
  const [showPICModal, setShowPICModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoType, setPhotoType] = useState<'before' | 'after'>('before');
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [customItemName, setCustomItemName] = useState('');
  const [customItemDescription, setCustomItemDescription] = useState('');
  const [customItemQuantity, setCustomItemQuantity] = useState(1);
  const [customItemUnitPrice, setCustomItemUnitPrice] = useState(0);
  const [inspectionNotes, setInspectionNotes] = useState('');
  // Form states
  const [inspectionDate, setInspectionDate] = useState('');
  const [inspectionTime, setInspectionTime] = useState('');
  const [jobDate, setJobDate] = useState('');
  const [jobTime, setJobTime] = useState('');
  const [picName, setPicName] = useState('');
  const [picPhone, setPicPhone] = useState('');
  const [picIdNumber, setPicIdNumber] = useState('');
  const [progressDescription, setProgressDescription] = useState('');
  // Load orders from localStorage on component mount
  useEffect(() => {
    const storedOrders = localStorage.getItem('vendorOrders');
    if (storedOrders) {
      setOrders(JSON.parse(storedOrders));
    } else {
      // Set some default orders if none exist
      const defaultOrders: Order[] = [{
        id: '1001',
        customerName: 'Ahmad Bin Abdullah',
        customerPhone: '+60 12-345-6789',
        customerAddress: '123 Jalan Bukit Bintang, Kuala Lumpur',
        serviceName: 'Bathroom Waterproofing',
        servicePrice: 850,
        status: 'pending',
        createdAt: '2023-10-15T08:30:00Z',
        customerPhotos: ['https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400', 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400']
      }, {
        id: '1002',
        customerName: 'Lim Wei Ling',
        customerPhone: '+60 13-456-7890',
        customerAddress: '45 Jalan Ampang, Kuala Lumpur',
        serviceName: 'Living Room Painting',
        servicePrice: 1200,
        status: 'inspection-scheduled',
        createdAt: '2023-10-14T10:15:00Z',
        inspectionDate: '2023-10-20',
        inspectionTime: '10:00 AM',
        customerPhotos: ['https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=400']
      }, {
        id: '1003',
        customerName: 'Rajesh Kumar',
        customerPhone: '+60 14-567-8901',
        customerAddress: '78 Jalan Imbi, Kuala Lumpur',
        serviceName: 'Door Repair',
        servicePrice: 250,
        status: 'in-progress',
        createdAt: '2023-10-12T14:45:00Z',
        inspectionDate: '2023-10-18',
        inspectionTime: '2:00 PM',
        jobDate: '2023-10-19',
        jobTime: '9:00 AM',
        picName: 'Ali bin Hassan',
        picPhone: '+60 12-999-8888',
        picIdNumber: '920101-10-5678',
        beforePhotos: ['https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=400'],
        progressUpdates: [{
          date: '2023-10-19T09:30:00Z',
          description: 'Started work, removed old door hinges',
          photos: ['https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=400']
        }, {
          date: '2023-10-19T14:00:00Z',
          description: 'Installed new hinges, door alignment complete'
        }]
      }, {
        id: '1004',
        customerName: 'Siti Nurhayati',
        customerPhone: '+60 15-678-9012',
        customerAddress: '92 Jalan Sultan Ismail, Kuala Lumpur',
        serviceName: 'Kitchen Waterproofing',
        servicePrice: 750,
        status: 'completed',
        createdAt: '2023-10-10T09:20:00Z',
        inspectionDate: '2023-10-15',
        inspectionTime: '10:00 AM',
        jobDate: '2023-10-16',
        jobTime: '8:00 AM',
        picName: 'Muhammad Faiz',
        picPhone: '+60 13-888-7777',
        picIdNumber: '880505-14-1234',
        beforePhotos: ['https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400'],
        afterPhotos: ['https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?w=400'],
        completedAt: '2023-10-16T17:00:00Z',
        progressUpdates: [{
          date: '2023-10-16T08:30:00Z',
          description: 'Surface preparation completed'
        }, {
          date: '2023-10-16T12:00:00Z',
          description: 'First coat of waterproofing applied'
        }, {
          date: '2023-10-16T16:00:00Z',
          description: 'Second coat applied, work completed'
        }],
        customerReview: {
          rating: 5,
          comment: 'Excellent work! Very professional and completed on time.',
          date: '2023-10-17T10:00:00Z'
        }
      }];
      setOrders(defaultOrders);
      localStorage.setItem('vendorOrders', JSON.stringify(defaultOrders));
    }
  }, []);
  // Apply filters whenever orders, statusFilter, or searchQuery changes
  useEffect(() => {
    let result = orders;
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order => order.customerName.toLowerCase().includes(query) || order.id.toLowerCase().includes(query) || order.serviceName.toLowerCase().includes(query));
    }
    setFilteredOrders(result);
  }, [orders, statusFilter, searchQuery]);
  // Save orders to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('vendorOrders', JSON.stringify(orders));
  }, [orders]);
  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'inspection-scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'inspected':
        return 'bg-indigo-100 text-indigo-800';
      case 'job-scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'in-progress':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'reviewed':
        return 'bg-emerald-100 text-emerald-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusLabel = (status: OrderStatus) => {
    const labels: Record<OrderStatus, string> = {
      pending: 'Pending',
      'inspection-scheduled': 'Inspection Scheduled',
      inspected: 'Inspected',
      'job-scheduled': 'Job Scheduled',
      'in-progress': 'In Progress',
      completed: 'Completed',
      reviewed: 'Reviewed',
      cancelled: 'Cancelled'
    };
    return labels[status];
  };
  const handleAddCustomItem = () => {
    if (!selectedOrder || !customItemName || customItemQuantity <= 0 || customItemUnitPrice <= 0) {
      alert('Please fill in all required fields');
      return;
    }
    const newItem: CustomItem = {
      id: Date.now().toString(),
      name: customItemName,
      description: customItemDescription,
      quantity: customItemQuantity,
      unitPrice: customItemUnitPrice,
      total: customItemQuantity * customItemUnitPrice
    };
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      customItems: [...(order.customItems || []), newItem]
    } : order);
    setOrders(updatedOrders);
    // Reset form
    setCustomItemName('');
    setCustomItemDescription('');
    setCustomItemQuantity(1);
    setCustomItemUnitPrice(0);
    alert('Custom item added successfully!');
  };
  const handleRemoveCustomItem = (itemId: string) => {
    if (!selectedOrder) return;
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      customItems: order.customItems?.filter(item => item.id !== itemId)
    } : order);
    setOrders(updatedOrders);
    alert('Custom item removed');
  };
  const calculateOrderTotal = (order: Order) => {
    const basePrice = order.servicePrice;
    const customItemsTotal = order.customItems?.reduce((sum, item) => sum + item.total, 0) || 0;
    return basePrice + customItemsTotal;
  };
  const calculateVendorEarnings = (order: Order) => {
    const totalAmount = calculateOrderTotal(order);
    const platformFee = totalAmount * 0.05; // 5% service fee
    const vendorEarnings = totalAmount - platformFee;
    return {
      totalAmount,
      platformFee,
      vendorEarnings
    };
  };
  const handleScheduleInspection = () => {
    if (!selectedOrder || !inspectionDate || !inspectionTime) return;
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      status: 'inspection-scheduled' as OrderStatus,
      inspectionDate,
      inspectionTime
    } : order);
    setOrders(updatedOrders);
    setShowInspectionModal(false);
    setInspectionDate('');
    setInspectionTime('');
    alert('Inspection scheduled successfully!');
  };
  const handleCompleteInspection = () => {
    if (!selectedOrder) return;
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      status: 'inspected' as OrderStatus,
      inspectionNotes
    } : order);
    setOrders(updatedOrders);
    setInspectionNotes('');
    alert('Inspection marked as complete! You can now add custom items if needed.');
  };
  const handleScheduleJob = () => {
    if (!selectedOrder || !jobDate || !jobTime) return;
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      status: 'job-scheduled' as OrderStatus,
      jobDate,
      jobTime
    } : order);
    setOrders(updatedOrders);
    setShowJobScheduleModal(false);
    setJobDate('');
    setJobTime('');
    alert('Job scheduled successfully!');
  };
  const handleAddPIC = () => {
    if (!selectedOrder || !picName || !picPhone || !picIdNumber) return;
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      status: 'in-progress' as OrderStatus,
      picName,
      picPhone,
      picIdNumber
    } : order);
    setOrders(updatedOrders);
    setShowPICModal(false);
    setPicName('');
    setPicPhone('');
    setPicIdNumber('');
    alert('PIC details added and job started!');
  };
  const handleAddProgress = () => {
    if (!selectedOrder || !progressDescription) return;
    const newUpdate = {
      date: new Date().toISOString(),
      description: progressDescription
    };
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      progressUpdates: [...(order.progressUpdates || []), newUpdate]
    } : order);
    setOrders(updatedOrders);
    setShowProgressModal(false);
    setProgressDescription('');
    alert('Progress update added!');
  };
  const handleCompleteJob = () => {
    if (!selectedOrder) return;
    if (!selectedOrder.beforePhotos || selectedOrder.beforePhotos.length === 0) {
      alert('Please upload before photos first!');
      return;
    }
    if (!selectedOrder.afterPhotos || selectedOrder.afterPhotos.length === 0) {
      alert('Please upload after photos first!');
      return;
    }
    const updatedOrders = orders.map(order => order.id === selectedOrder.id ? {
      ...order,
      status: 'completed' as OrderStatus,
      completedAt: new Date().toISOString()
    } : order);
    setOrders(updatedOrders);
    setShowDetailModal(false);
    alert('Job marked as completed! Customer has 3 days to review, or it will be auto-approved.');
  };
  const handleUploadPhoto = (type: 'before' | 'after') => {
    // In a real app, this would handle actual file upload
    const mockPhotoUrl = 'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400';
    const updatedOrders = orders.map(order => {
      if (order.id === selectedOrder?.id) {
        if (type === 'before') {
          return {
            ...order,
            beforePhotos: [...(order.beforePhotos || []), mockPhotoUrl]
          };
        } else {
          return {
            ...order,
            afterPhotos: [...(order.afterPhotos || []), mockPhotoUrl]
          };
        }
      }
      return order;
    });
    setOrders(updatedOrders);
    alert(`${type === 'before' ? 'Before' : 'After'} photo uploaded successfully!`);
  };
  const openDetailModal = (order: Order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };
  return <div data-id="element-1670">
      <div className="mb-6" data-id="element-1671">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1672">Job Orders</h1>
        <p className="text-gray-600 mt-1" data-id="element-1673">
          Manage customer orders and track progress
        </p>
      </div>
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6" data-id="element-1674">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1675">
          <div className="relative flex-1" data-id="element-1676">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1677">
              <Search size={18} className="text-gray-400" data-id="element-1678" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" placeholder="Search by customer name or order ID..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1679" />
          </div>
          <div className="flex items-center" data-id="element-1680">
            <Filter size={18} className="text-gray-500 mr-2" data-id="element-1681" />
            <select className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-primary focus:border-primary sm:text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value as OrderStatus | 'all')} data-id="element-1682">
              <option value="all" data-id="element-1683">All Orders</option>
              <option value="pending" data-id="element-1684">Pending</option>
              <option value="inspection-scheduled" data-id="element-1685">Inspection Scheduled</option>
              <option value="inspected" data-id="element-1686">Inspected</option>
              <option value="job-scheduled" data-id="element-1687">Job Scheduled</option>
              <option value="in-progress" data-id="element-1688">In Progress</option>
              <option value="completed" data-id="element-1689">Completed</option>
              <option value="reviewed" data-id="element-1690">Reviewed</option>
              <option value="cancelled" data-id="element-1691">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      <div className="space-y-4" data-id="element-1692">
        {filteredOrders.length === 0 ? <div className="bg-white p-8 text-center rounded-lg shadow-sm border border-gray-200" data-id="element-1693">
            <p className="text-gray-500" data-id="element-1694">
              No orders found matching your criteria.
            </p>
          </div> : filteredOrders.map(order => <div key={order.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-1695">
              <div className="p-4" data-id="element-1696">
                <div className="flex justify-between items-start" data-id="element-1697">
                  <div className="flex-1" data-id="element-1698">
                    <div className="flex items-center justify-between mb-2" data-id="element-1699">
                      <h3 className="text-lg font-medium text-gray-900" data-id="element-1700">
                        Order #{order.id}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-id="element-1701">
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2" data-id="element-1702">
                      {order.serviceName}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600" data-id="element-1703">
                      <div className="flex items-center" data-id="element-1704">
                        <User size={16} className="mr-2 text-gray-400" data-id="element-1705" />
                        <span data-id="element-1706">{order.customerName}</span>
                      </div>
                      <div className="flex items-center" data-id="element-1707">
                        <Phone size={16} className="mr-2 text-gray-400" data-id="element-1708" />
                        <span data-id="element-1709">{order.customerPhone}</span>
                      </div>
                      <div className="flex items-start md:col-span-2" data-id="element-1710">
                        <MapPin size={16} className="mr-2 text-gray-400 mt-0.5" data-id="element-1711" />
                        <span data-id="element-1712">{order.customerAddress}</span>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 text-right" data-id="element-1713">
                    <p className="text-lg font-bold text-gray-900" data-id="element-1714">
                      MYR {order.servicePrice.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end space-x-2" data-id="element-1715">
                  <button onClick={() => openDetailModal(order)} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50" data-id="element-1716">
                    View Details
                  </button>
                  {order.status === 'pending' && <button onClick={() => {
              setSelectedOrder(order);
              setShowInspectionModal(true);
            }} className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90" data-id="element-1717">
                      Schedule Inspection
                    </button>}
                  {order.status === 'inspected' && <button onClick={() => {
              setSelectedOrder(order);
              setShowJobScheduleModal(true);
            }} className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90" data-id="element-1718">
                      Schedule Job
                    </button>}
                  {order.status === 'job-scheduled' && <button onClick={() => {
              setSelectedOrder(order);
              setShowPICModal(true);
            }} className="px-4 py-2 bg-primary text-white text-sm rounded-md hover:bg-primary/90" data-id="element-1719">
                      Start Job
                    </button>}
                  {order.status === 'in-progress' && <>
                      <button onClick={() => {
                setSelectedOrder(order);
                setShowProgressModal(true);
              }} className="px-4 py-2 border border-primary text-primary text-sm rounded-md hover:bg-primary/5" data-id="element-1720">
                        Add Progress
                      </button>
                      <button onClick={() => {
                setSelectedOrder(order);
                handleCompleteJob();
              }} className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700" data-id="element-1721">
                        Complete Job
                      </button>
                    </>}
                </div>
              </div>
            </div>)}
      </div>
      {/* Detail Modal */}
      {showDetailModal && selectedOrder && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-1722">
          <div className="bg-white rounded-lg max-w-4xl w-full my-8" data-id="element-1723">
            <div className="p-6 border-b border-gray-200" data-id="element-1724">
              <div className="flex justify-between items-start" data-id="element-1725">
                <div data-id="element-1726">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1727">
                    Order #{selectedOrder.id}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-1728">
                    {selectedOrder.serviceName}
                  </p>
                </div>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-1729">
                  <span className="text-2xl" data-id="element-1730">&times;</span>
                </button>
              </div>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto" data-id="element-1731">
              {/* Customer Photos */}
              {selectedOrder.customerPhotos && selectedOrder.customerPhotos.length > 0 && <div className="mb-6" data-id="element-1732">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center" data-id="element-1733">
                      <ImageIcon className="h-5 w-5 mr-2 text-primary" data-id="element-1734" />
                      Customer Uploaded Photos
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1735">
                      {selectedOrder.customerPhotos.map((photo, index) => <img key={index} src={photo} alt={`Customer photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" data-id="element-1736" />)}
                    </div>
                  </div>}
              {/* Inspection Schedule */}
              {selectedOrder.inspectionDate && <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1737">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-1738">
                    <Calendar className="h-5 w-5 mr-2 text-blue-600" data-id="element-1739" />
                    Inspection Schedule
                  </h4>
                  <p className="text-sm text-gray-700" data-id="element-1740">
                    Date: {selectedOrder.inspectionDate} at{' '}
                    {selectedOrder.inspectionTime}
                  </p>
                  {selectedOrder.inspectionNotes && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-1741">
                      <p className="text-xs font-medium text-gray-700 mb-1" data-id="element-1742">
                        Inspection Notes:
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-1743">
                        {selectedOrder.inspectionNotes}
                      </p>
                    </div>}
                  {selectedOrder.status === 'inspection-scheduled' && <div className="mt-3 space-y-3" data-id="element-1744">
                      <div data-id="element-1745">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1746">
                          Inspection Notes (Optional)
                        </label>
                        <textarea rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary" placeholder="Add any notes from the inspection..." value={inspectionNotes} onChange={e => setInspectionNotes(e.target.value)} data-id="element-1747" />
                      </div>
                      <button onClick={handleCompleteInspection} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" data-id="element-1748">
                        Mark Inspection Complete
                      </button>
                    </div>}
                </div>}
              {/* Custom Items Section */}
              {(selectedOrder.status === 'inspected' || selectedOrder.customItems) && <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-100" data-id="element-1749">
                  <div className="flex items-center justify-between mb-3" data-id="element-1750">
                    <h4 className="font-medium text-gray-900 flex items-center" data-id="element-1751">
                      <Plus className="h-5 w-5 mr-2 text-purple-600" data-id="element-1752" />
                      Additional Items
                    </h4>
                    {selectedOrder.status === 'inspected' && <button onClick={() => setShowCustomItemModal(true)} className="px-3 py-1 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 flex items-center" data-id="element-1753">
                        <Plus className="h-4 w-4 mr-1" data-id="element-1754" />
                        Add Item
                      </button>}
                  </div>
                  {selectedOrder.customItems && selectedOrder.customItems.length > 0 ? <div className="space-y-3" data-id="element-1755">
                      {selectedOrder.customItems.map(item => <div key={item.id} className="bg-white p-4 rounded-lg border border-purple-200" data-id="element-1756">
                          <div className="flex justify-between items-start" data-id="element-1757">
                            <div className="flex-1" data-id="element-1758">
                              <h5 className="font-medium text-gray-900" data-id="element-1759">
                                {item.name}
                              </h5>
                              {item.description && <p className="text-sm text-gray-600 mt-1" data-id="element-1760">
                                  {item.description}
                                </p>}
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-700" data-id="element-1761">
                                <span data-id="element-1762">Qty: {item.quantity}</span>
                                <span data-id="element-1763">•</span>
                                <span data-id="element-1764">
                                  Unit Price: MYR {item.unitPrice.toFixed(2)}
                                </span>
                                <span data-id="element-1765">•</span>
                                <span className="font-medium" data-id="element-1766">
                                  Total: MYR {item.total.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            {selectedOrder.status === 'inspected' && <button onClick={() => handleRemoveCustomItem(item.id)} className="ml-3 text-red-500 hover:text-red-700" data-id="element-1767">
                                <Trash2 className="h-4 w-4" data-id="element-1768" />
                              </button>}
                          </div>
                        </div>)}
                      <div className="pt-3 border-t border-purple-200" data-id="element-1769">
                        <div className="flex justify-between items-center text-sm" data-id="element-1770">
                          <span className="text-gray-700" data-id="element-1771">
                            Custom Items Subtotal:
                          </span>
                          <span className="font-bold text-gray-900" data-id="element-1772">
                            MYR{' '}
                            {selectedOrder.customItems.reduce((sum, item) => sum + item.total, 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div> : <p className="text-sm text-gray-600" data-id="element-1773">
                      No additional items added. Click "Add Item" to include any
                      extra work discovered during inspection.
                    </p>}
                </div>}
              {/* Order Total with Vendor Earnings */}
              {selectedOrder.customItems && selectedOrder.customItems.length > 0 || selectedOrder.status === 'completed' || selectedOrder.status === 'reviewed' ? <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1774">
                  <h4 className="font-medium text-gray-900 mb-3" data-id="element-1775">
                    Order Summary
                  </h4>
                  <div className="space-y-2 text-sm" data-id="element-1776">
                    <div className="flex justify-between" data-id="element-1777">
                      <span className="text-gray-700" data-id="element-1778">Base Service:</span>
                      <span className="text-gray-900" data-id="element-1779">
                        MYR {selectedOrder.servicePrice.toFixed(2)}
                      </span>
                    </div>
                    {selectedOrder.customItems && selectedOrder.customItems.length > 0 && <div className="flex justify-between" data-id="element-1780">
                          <span className="text-gray-700" data-id="element-1781">
                            Additional Items:
                          </span>
                          <span className="text-gray-900" data-id="element-1782">
                            MYR{' '}
                            {selectedOrder.customItems.reduce((sum, item) => sum + item.total, 0).toFixed(2)}
                          </span>
                        </div>}
                    <div className="pt-2 border-t border-gray-300 flex justify-between items-center" data-id="element-1783">
                      <span className="font-bold text-gray-900" data-id="element-1784">
                        Total Amount:
                      </span>
                      <span className="text-xl font-bold text-gray-900" data-id="element-1785">
                        MYR {calculateOrderTotal(selectedOrder).toFixed(2)}
                      </span>
                    </div>
                    {/* Vendor Earnings Breakdown */}
                    <div className="mt-4 pt-4 border-t-2 border-gray-300 bg-blue-50 -mx-4 -mb-4 p-4 rounded-b-lg" data-id="element-1786">
                      <div className="flex items-center mb-3" data-id="element-1787">
                        <DollarSign className="h-5 w-5 text-blue-600 mr-2" data-id="element-1788" />
                        <h5 className="font-bold text-gray-900" data-id="element-1789">
                          Your Earnings
                        </h5>
                      </div>
                      <div className="space-y-2" data-id="element-1790">
                        <div className="flex justify-between text-sm" data-id="element-1791">
                          <span className="text-gray-700" data-id="element-1792">Order Total:</span>
                          <span className="text-gray-900" data-id="element-1793">
                            MYR{' '}
                            {calculateVendorEarnings(selectedOrder).totalAmount.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm items-center" data-id="element-1794">
                          <span className="text-gray-700 flex items-center" data-id="element-1795">
                            <TrendingDown className="h-4 w-4 mr-1 text-red-500" data-id="element-1796" />
                            Platform Service Fee (5%):
                          </span>
                          <span className="text-red-600" data-id="element-1797">
                            - MYR{' '}
                            {calculateVendorEarnings(selectedOrder).platformFee.toFixed(2)}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-blue-200 flex justify-between items-center" data-id="element-1798">
                          <span className="font-bold text-gray-900" data-id="element-1799">
                            You Will Receive:
                          </span>
                          <span className="text-xl font-bold text-green-600" data-id="element-1800">
                            MYR{' '}
                            {calculateVendorEarnings(selectedOrder).vendorEarnings.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-3" data-id="element-1801">
                        * Payment will be processed within 3-5 business days
                        after order completion
                      </p>
                    </div>
                  </div>
                </div> : <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1802">
                  <div className="flex justify-between items-center" data-id="element-1803">
                    <span className="font-medium text-gray-900" data-id="element-1804">
                      Order Amount:
                    </span>
                    <span className="text-xl font-bold text-primary" data-id="element-1805">
                      MYR {selectedOrder.servicePrice.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2" data-id="element-1806">
                    * 5% platform fee will be deducted from final amount
                  </p>
                </div>}
              {/* Job Schedule */}
              {selectedOrder.jobDate && <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-100" data-id="element-1807">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-1808">
                    <Calendar className="h-5 w-5 mr-2 text-purple-600" data-id="element-1809" />
                    Job Schedule
                  </h4>
                  <p className="text-sm text-gray-700" data-id="element-1810">
                    Date: {selectedOrder.jobDate} at {selectedOrder.jobTime}
                  </p>
                </div>}
              {/* PIC Details */}
              {selectedOrder.picName && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1811">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center" data-id="element-1812">
                    <User className="h-5 w-5 mr-2 text-gray-600" data-id="element-1813" />
                    Person In Charge (PIC)
                  </h4>
                  <div className="space-y-2 text-sm text-gray-700" data-id="element-1814">
                    <p data-id="element-1815">
                      <span className="font-medium" data-id="element-1816">Name:</span>{' '}
                      {selectedOrder.picName}
                    </p>
                    <p data-id="element-1817">
                      <span className="font-medium" data-id="element-1818">Phone:</span>{' '}
                      {selectedOrder.picPhone}
                    </p>
                    <p data-id="element-1819">
                      <span className="font-medium" data-id="element-1820">ID Number:</span>{' '}
                      {selectedOrder.picIdNumber}
                    </p>
                  </div>
                </div>}
              {/* Before Photos */}
              <div className="mb-6" data-id="element-1821">
                <div className="flex items-center justify-between mb-3" data-id="element-1822">
                  <h4 className="font-medium text-gray-900 flex items-center" data-id="element-1823">
                    <Camera className="h-5 w-5 mr-2 text-primary" data-id="element-1824" />
                    Before Photos
                    <span className="ml-2 text-red-600 text-xs" data-id="element-1825">*Required</span>
                  </h4>
                  {selectedOrder.status === 'in-progress' && <button onClick={() => handleUploadPhoto('before')} className="px-3 py-1 border border-primary text-primary text-sm rounded-md hover:bg-primary/5" data-id="element-1826">
                      <Upload className="h-4 w-4 inline mr-1" data-id="element-1827" />
                      Upload
                    </button>}
                </div>
                {selectedOrder.beforePhotos && selectedOrder.beforePhotos.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1828">
                    {selectedOrder.beforePhotos.map((photo, index) => <img key={index} src={photo} alt={`Before photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" data-id="element-1829" />)}
                  </div> : <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm" data-id="element-1830">
                    No before photos uploaded yet
                  </div>}
              </div>
              {/* Progress Updates */}
              {selectedOrder.progressUpdates && selectedOrder.progressUpdates.length > 0 && <div className="mb-6" data-id="element-1831">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center" data-id="element-1832">
                      <Clock className="h-5 w-5 mr-2 text-primary" data-id="element-1833" />
                      Progress Updates
                    </h4>
                    <div className="space-y-3" data-id="element-1834">
                      {selectedOrder.progressUpdates.map((update, index) => <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1835">
                          <p className="text-sm text-gray-700" data-id="element-1836">
                            {update.description}
                          </p>
                          <p className="text-xs text-gray-500 mt-1" data-id="element-1837">
                            {new Date(update.date).toLocaleString()}
                          </p>
                        </div>)}
                    </div>
                  </div>}
              {/* After Photos */}
              <div className="mb-6" data-id="element-1838">
                <div className="flex items-center justify-between mb-3" data-id="element-1839">
                  <h4 className="font-medium text-gray-900 flex items-center" data-id="element-1840">
                    <Camera className="h-5 w-5 mr-2 text-primary" data-id="element-1841" />
                    After Photos
                    <span className="ml-2 text-red-600 text-xs" data-id="element-1842">*Required</span>
                  </h4>
                  {selectedOrder.status === 'in-progress' && <button onClick={() => handleUploadPhoto('after')} className="px-3 py-1 border border-primary text-primary text-sm rounded-md hover:bg-primary/5" data-id="element-1843">
                      <Upload className="h-4 w-4 inline mr-1" data-id="element-1844" />
                      Upload
                    </button>}
                </div>
                {selectedOrder.afterPhotos && selectedOrder.afterPhotos.length > 0 ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1845">
                    {selectedOrder.afterPhotos.map((photo, index) => <img key={index} src={photo} alt={`After photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" data-id="element-1846" />)}
                  </div> : <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm" data-id="element-1847">
                    No after photos uploaded yet
                  </div>}
              </div>
              {/* Completion Info */}
              {selectedOrder.completedAt && <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-100" data-id="element-1848">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-1849">
                    <CheckCircle className="h-5 w-5 mr-2 text-green-600" data-id="element-1850" />
                    Job Completed
                  </h4>
                  <p className="text-sm text-gray-700" data-id="element-1851">
                    Completed on:{' '}
                    {new Date(selectedOrder.completedAt).toLocaleString()}
                  </p>
                  {!selectedOrder.customerReview && <p className="text-sm text-gray-600 mt-2" data-id="element-1852">
                      Waiting for customer review (auto-approve in 3 days)
                    </p>}
                </div>}
              {/* Customer Review */}
              {selectedOrder.customerReview && <div className="mb-6 p-4 bg-yellow-50 rounded-lg border border-yellow-100" data-id="element-1853">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center" data-id="element-1854">
                    <Star className="h-5 w-5 mr-2 text-yellow-600" data-id="element-1855" />
                    Customer Review
                  </h4>
                  <div className="flex items-center mb-2" data-id="element-1856">
                    {[...Array(5)].map((_, i) => <Star key={i} className={`h-5 w-5 ${i < selectedOrder.customerReview!.rating ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} data-id="element-1857" />)}
                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-1858">
                      {selectedOrder.customerReview.rating} / 5
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2" data-id="element-1859">
                    {selectedOrder.customerReview.comment}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1860">
                    Reviewed on:{' '}
                    {new Date(selectedOrder.customerReview.date).toLocaleDateString()}
                  </p>
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1861">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1862">
                Close
              </button>
            </div>
          </div>
        </div>}
      {/* Custom Item Modal */}
      {showCustomItemModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1863">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-1864">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1865">
              Add Custom Item
            </h3>
            <div className="space-y-4" data-id="element-1866">
              <div data-id="element-1867">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1868">
                  Item Name <span className="text-red-500" data-id="element-1869">*</span>
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={customItemName} onChange={e => setCustomItemName(e.target.value)} placeholder="e.g., Additional paint for ceiling" data-id="element-1870" />
              </div>
              <div data-id="element-1871">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1872">
                  Description (Optional)
                </label>
                <textarea rows={2} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={customItemDescription} onChange={e => setCustomItemDescription(e.target.value)} placeholder="Add any additional details..." data-id="element-1873" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1874">
                <div data-id="element-1875">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1876">
                    Quantity <span className="text-red-500" data-id="element-1877">*</span>
                  </label>
                  <input type="number" min="1" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={customItemQuantity} onChange={e => setCustomItemQuantity(Number(e.target.value))} data-id="element-1878" />
                </div>
                <div data-id="element-1879">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1880">
                    Unit Price (MYR) <span className="text-red-500" data-id="element-1881">*</span>
                  </label>
                  <input type="number" min="0" step="0.01" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={customItemUnitPrice} onChange={e => setCustomItemUnitPrice(Number(e.target.value))} data-id="element-1882" />
                </div>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1883">
                <div className="flex justify-between items-center" data-id="element-1884">
                  <span className="text-sm font-medium text-gray-700" data-id="element-1885">
                    Total:
                  </span>
                  <span className="text-lg font-bold text-primary" data-id="element-1886">
                    MYR {(customItemQuantity * customItemUnitPrice).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-1887">
              <button onClick={() => {
            setShowCustomItemModal(false);
            setCustomItemName('');
            setCustomItemDescription('');
            setCustomItemQuantity(1);
            setCustomItemUnitPrice(0);
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1888">
                Cancel
              </button>
              <button onClick={handleAddCustomItem} disabled={!customItemName || customItemQuantity <= 0 || customItemUnitPrice <= 0} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-1889">
                Add Item
              </button>
            </div>
          </div>
        </div>}
      {/* Inspection Modal */}
      {showInspectionModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1890">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-1891">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1892">
              Schedule Inspection
            </h3>
            <div className="space-y-4" data-id="element-1893">
              <div data-id="element-1894">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1895">
                  Inspection Date
                </label>
                <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={inspectionDate} onChange={e => setInspectionDate(e.target.value)} data-id="element-1896" />
              </div>
              <div data-id="element-1897">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1898">
                  Inspection Time
                </label>
                <input type="time" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={inspectionTime} onChange={e => setInspectionTime(e.target.value)} data-id="element-1899" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-1900">
              <button onClick={() => setShowInspectionModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1901">
                Cancel
              </button>
              <button onClick={handleScheduleInspection} disabled={!inspectionDate || !inspectionTime} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-1902">
                Schedule
              </button>
            </div>
          </div>
        </div>}
      {/* Job Schedule Modal */}
      {showJobScheduleModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1903">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-1904">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1905">
              Schedule Job
            </h3>
            <div className="space-y-4" data-id="element-1906">
              <div data-id="element-1907">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1908">
                  Job Date
                </label>
                <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={jobDate} onChange={e => setJobDate(e.target.value)} data-id="element-1909" />
              </div>
              <div data-id="element-1910">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1911">
                  Job Time
                </label>
                <input type="time" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={jobTime} onChange={e => setJobTime(e.target.value)} data-id="element-1912" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-1913">
              <button onClick={() => setShowJobScheduleModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1914">
                Cancel
              </button>
              <button onClick={handleScheduleJob} disabled={!jobDate || !jobTime} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-1915">
                Schedule
              </button>
            </div>
          </div>
        </div>}
      {/* PIC Modal */}
      {showPICModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1916">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-1917">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1918">
              Add Person In Charge (PIC) Details
            </h3>
            <div className="space-y-4" data-id="element-1919">
              <div data-id="element-1920">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1921">
                  PIC Name
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={picName} onChange={e => setPicName(e.target.value)} placeholder="Enter PIC name" data-id="element-1922" />
              </div>
              <div data-id="element-1923">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1924">
                  PIC Phone Number
                </label>
                <input type="tel" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={picPhone} onChange={e => setPicPhone(e.target.value)} placeholder="+60 12-345-6789" data-id="element-1925" />
              </div>
              <div data-id="element-1926">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1927">
                  PIC ID Number
                </label>
                <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={picIdNumber} onChange={e => setPicIdNumber(e.target.value)} placeholder="XXXXXX-XX-XXXX" data-id="element-1928" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-1929">
              <button onClick={() => setShowPICModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1930">
                Cancel
              </button>
              <button onClick={handleAddPIC} disabled={!picName || !picPhone || !picIdNumber} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-1931">
                Start Job
              </button>
            </div>
          </div>
        </div>}
      {/* Progress Modal */}
      {showProgressModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1932">
          <div className="bg-white rounded-lg max-w-md w-full p-6" data-id="element-1933">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1934">
              Add Progress Update
            </h3>
            <div className="space-y-4" data-id="element-1935">
              <div data-id="element-1936">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1937">
                  Progress Description
                </label>
                <textarea rows={4} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary" value={progressDescription} onChange={e => setProgressDescription(e.target.value)} placeholder="Describe the work completed..." data-id="element-1938" />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3" data-id="element-1939">
              <button onClick={() => setShowProgressModal(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50" data-id="element-1940">
                Cancel
              </button>
              <button onClick={handleAddProgress} disabled={!progressDescription.trim()} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-id="element-1941">
                Add Update
              </button>
            </div>
          </div>
        </div>}
    </div>;
}